import React, { useState, useEffect, useRef } from "react";

// Bilder
import envKontor from "@/assets/env_kontor.webp";
import envFrukostDining from "@/assets/env_frukost_dining.webp";
import envFrukostStilllife from "@/assets/env_frukost_stilllife.webp";
import envVantrum from "@/assets/env_vantrum.webp";
import envKok from "@/assets/env_kok.webp";
import envRestaurangDispenser from "@/assets/env_restaurang_dispenser.webp";
import envSallad from "@/assets/env_sallad.webp";
import envLounge from "@/assets/env_lounge.webp";
import envDispenserLobby from "@/assets/env_dispenser_lobby.webp";
import envArmchair from "@/assets/env_armchair.webp";
import mbIcecubes from "@/assets/moodboard_icecubes.webp";
import mbCitrus from "@/assets/moodboard_citrus.webp";
import mbDispenser1 from "@/assets/moodboard_dispenser_1.webp";
import mbDispenser2 from "@/assets/moodboard_dispenser_2.webp";
import mbDispenser3 from "@/assets/moodboard_dispenser_3.webp";
import mbDispenser4 from "@/assets/moodboard_dispenser_4.webp";
import mbDispenser5 from "@/assets/moodboard_dispenser_5.webp";
import mbHolder1 from "@/assets/moodboard_holder_1.webp";
import mbHolder2 from "@/assets/moodboard_holder_2.webp";
import mbKitchen from "@/assets/moodboard_kitchen.webp";
import mbCafe from "@/assets/moodboard_cafe.webp";
import mbTablets from "@/assets/moodboard_tablets.webp";
import mbOfficeBlue from "@/assets/moodboard_office_blue.webp";
import mbHomeoffice from "@/assets/moodboard_homeoffice.webp";

// Videor
import mbVid1 from "@/assets/moodboard_vid_1.webm";
import mbVid2 from "@/assets/moodboard_vid_2.webm";
import mbVid3 from "@/assets/moodboard_vid_3.webm";
import mbVid4 from "@/assets/moodboard_vid_4.webm";
import mbVid5 from "@/assets/moodboard_vid_5.webm";
import mbVidWorkplace from "@/assets/moodboard_vid_workplace.webm";
import mbVid6 from "@/assets/moodboard_vid_6.webm";
import mbVid7 from "@/assets/moodboard_vid_7.webm";
import mbVid8 from "@/assets/moodboard_vid_8.webm";

type MediaItem = { src: string; type: "image" | "video"; wide?: boolean };

const img = (src: string, wide = false): MediaItem => ({ src, type: "image", wide });
const vid = (src: string, wide = false): MediaItem => ({ src, type: "video", wide });

const topRow: MediaItem[] = [
  vid(mbVidWorkplace, true), img(mbDispenser1), img(envFrukostDining),
  vid(mbVid5, true), img(mbHolder1), img(mbCitrus, true), img(envSallad),
  img(mbKitchen), vid(mbVid3),
];

const midRow: MediaItem[] = [
  img(envVantrum), img(mbDispenser2), vid(mbVid2, true),
  img(mbTablets, true), img(envRestaurangDispenser), vid(mbVid1), img(mbCafe), vid(mbVid4),
  img(envFrukostStilllife), img(mbOfficeBlue), img(mbHolder2), img(envLounge, true),
  img(mbDispenser3),
];

const botRow: MediaItem[] = [
  img(envKok), vid(mbVid6, true), img(mbDispenser4),
  img(envDispenserLobby), img(mbHomeoffice, true), vid(mbVid8, true), img(mbDispenser5), vid(mbVid7),
  img(envArmchair, true), img(mbIcecubes, true),
  img(envKontor),
];

interface FilmstripRowProps {
  items: MediaItem[];
  duration: number;
  reverse?: boolean;
}

const FilmstripRow: React.FC<FilmstripRowProps> = ({ items, duration, reverse = false }) => {
  const doubled = [...items, ...items];
  return (
    <div className="relative h-1/3 overflow-hidden">
      <div
        className="flex h-full gap-2 w-max"
        style={{
          animation: `marquee-scroll ${duration}s linear infinite`,
          animationDirection: reverse ? "reverse" : "normal",
        }}
      >
        {doubled.map((item, i) => (
          <div
            key={i}
            className={`h-full flex-shrink-0 ${item.wide ? "w-[28rem]" : "w-64"} overflow-hidden rounded-sm`}
          >
            {item.type === "video" ? (
              <video
                src={item.src}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={item.src}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface MoodboardHeroProps {
  className?: string;
}

// Matchar /om-oss hero: from-coral-500/20 via-cream-100 to-cream-50
const GRADIENT_BG =
  "linear-gradient(to bottom, rgba(123, 186, 212, 0.2) 0%, #F5F1E8 50%, #FAF7F0 100%)";

const MoodboardHero: React.FC<MoodboardHeroProps> = ({ className = "h-[85vh]" }) => {
  const [wipeOut, setWipeOut] = useState(false);
  const [galleryVisible, setGalleryVisible] = useState(false);
  const hasPlayed = useRef(false);

  useEffect(() => {
    if (hasPlayed.current) return;
    hasPlayed.current = true;
    // Litet håll innan wipen drar igång
    const t1 = setTimeout(() => {
      setGalleryVisible(true);
      setWipeOut(true);
    }, 300);
    return () => clearTimeout(t1);
  }, []);

  return (
    <section className={`relative w-full overflow-hidden ${className}`}>
      {/* Statisk gradient-bakgrund (matchar sajtens cream→terracotta-palett) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: GRADIENT_BG }}
      />

      {/* Tre filmstrip-rader */}
      <div
        className="absolute inset-0 flex flex-col gap-2"
        style={{
          opacity: galleryVisible ? 1 : 0,
          transition: "opacity 0.4s ease-out",
        }}
      >
        <FilmstripRow items={topRow} duration={140} />
        <FilmstripRow items={midRow} duration={180} reverse />
        <FilmstripRow items={botRow} duration={160} />
      </div>

      {/* Wipe-overlay: samma gradient ovanpå galleriet som svepar ut åt höger */}
      <div
        className="absolute inset-0 pointer-events-none z-[5]"
        style={{
          background: GRADIENT_BG,
          transform: wipeOut ? "translateX(100%)" : "translateX(0)",
          transition: "transform 1.1s cubic-bezier(0.77, 0, 0.175, 1)",
        }}
      />

      {/* Mörk overlay för text-läsbarhet (fokus i nedre vänstra hörnet) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 90% 70% at 20% 85%, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0) 75%)",
          opacity: galleryVisible ? 1 : 0,
          transition: "opacity 0.6s ease-out",
        }}
      />

      {/* Mjuk fade i botten mot nästa sektion (background-färg) */}
      <div
        className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, hsl(var(--background) / 0) 0%, hsl(var(--background) / 0.6) 60%, hsl(var(--background)) 100%)",
          opacity: galleryVisible ? 1 : 0,
          transition: "opacity 0.6s ease-out",
        }}
      />



      {/* Text-overlay */}
      <div
        className="absolute bottom-10 md:bottom-16 left-6 md:left-12 right-6 md:right-12 text-white pointer-events-none z-10"
        style={{
          opacity: galleryVisible ? 1 : 0,
          transform: galleryVisible ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s",
        }}
      >
        <h1 className="text-6xl md:text-8xl lg:text-9xl font-black uppercase tracking-tight leading-[0.9] mb-4">
          Vår<br />Tjänst
        </h1>
        <p className="text-sm md:text-base max-w-md text-white/85 mb-6">
          En komplett vitaminlösning för arbetsplatsen. Dispenser, brustabletter och smidig påfyllning, allt på ett ställe.
        </p>
        <a
          href="/pricing"
          className="inline-block text-sm font-bold tracking-[0.2em] uppercase border-b-2 border-white pb-1 hover:opacity-80 transition-opacity pointer-events-auto"
        >
          Beställ →
        </a>
      </div>
    </section>
  );
};

export default MoodboardHero;
